import type { LDContext, LDContextCommon } from "@launchdarkly/node-server-sdk";

/**
 * Feature flag keys sourced from LaunchDarkly. Each member's value must match
 * the corresponding flag key configured in LaunchDarkly exactly.
 */
export enum Flag {
  EMAIL_OTP_LOGIN = "email-otp-login",
  /** Client-only: Meta in-app browsers request a login link without PKCE. */
  META_WEBVIEW_LINK_LOGIN = "meta-webview-link-login",
  ENDPOINT_CDP_PROVIDER_OVERRIDE = "endpoint-cdp-provider-override",
  RPA_PROXY_ASSIGNMENT = "rpa-proxy-assignment",
  RPA_CREEPJS_DETECTOR_URL = "rpa-creepjs-detector-url",
  RPA_CREEPJS_PROBE = "rpa-creepjs-probe",
  RPA_WIRE_REFLECTOR_URL = "rpa-wire-reflector-url",
  RPA_WIRE_PROBE = "rpa-wire-probe",
  RPA_REBROWSER_DETECTOR_URL = "rpa-rebrowser-detector-url",
  RPA_REBROWSER_PROBE = "rpa-rebrowser-probe",
  SIGN_IN_HEDGE_COUNT = "sign-in-hedge-count",
  SIGN_IN_PASSKEY_WEBAUTHN = "sign-in-passkey-webauthn",
  PLAYWRIGHT_CAPTURE_HAR_ARTIFACT = "playwright-capture-har-artifact",
  PLAYWRIGHT_FILL_STRATEGY = "playwright-fill-strategy",
  BROWSERBASE_CUSTOM_SESSION_SETTINGS = "browserbase-custom-session-settings",
  STEEL_USE_STEALTH_BROWSER = "steel-use-stealth-browser",
  UNDETECT_CUSTOM_LAUNCH_ENV = "undetect-custom-launch-env",
  UNDETECT_CLUSTER = "undetect-launch-cluster",
  RUN_CLASSIC_CRAWL = "run-classic-crawl",
  RUN_AI_CRAWL = "run-ai-crawl",
  CRAWL_ENABLE_DISCOVERY = "crawl-enable-discovery",
  COMPARE_FAIR_MARKET_VALUE = "compare-fair-market-value",
  AI_CRAWL_ENABLE_CLAIMS_LIST_DISCOVERY = "ai-crawl-enable-claims-list-discovery",
  AI_CRAWL_ENABLE_CLAIM_DETAILS_VISITS = "ai-crawl-enable-claim-details-visits",
  AI_CRAWL_ENABLE_PLAN_DOCUMENTS = "ai-crawl-enable-plan-documents",
  AI_CRAWL_ENABLE_MEMBER_ID_CARDS = "ai-crawl-enable-member-id-cards",
  AI_CRAWL_ENABLE_MFA_CONFIGURATION_DISCOVERY = "ai-crawl-enable-mfa-configuration-discovery",
  AI_CRAWL_ENABLE_ACCUMULATORS_DISCOVERY = "ai-crawl-enable-accumulators-discovery",
  AI_CRAWL_ENABLE_PARALLELIZATION = "enable-ai-crawl-parallelization",
  /** Uses ingestion V2 for document uploads; otherwise retains legacy extraction. */
  DOC_SUMMARY_FROM_INGESTION_V2 = "doc-summary-from-ingestion-v2",
  /** Enables guided intake within ingestion V2, with durable document stages. */
  DOCUMENT_INTAKE_V2_ENABLED = "document-intake-v2-enabled",
  /** Withholds machine amount suggestions from invoices that fail validation. */
  BILL_AMOUNT_VALIDATION_ENABLED = "bill-amount-validation-enabled",
  /**
   * Gates every `CasePriorPaymentReceived` writer: tasks predating the event
   * cannot decode the case, so enable only after API and event-worker roll over.
   */
  PRIOR_PAYMENT_ENABLED = "prior-payment-enabled",
  /**
   * Gates every `BillUploadRequested` writer. Enable only after the API and
   * event-worker fleet has fully rolled onto a release that can decode it.
   */
  BILL_UPLOAD_REQUEST_EVENTS_ENABLED = "bill-upload-request-events-enabled",
  /** Shared rollout gate for the chat-streaming project: streamed card
   *  messages, the turn tail fetch, intake waits and the turn-backed
   *  barrier message, free-text reply, and immediate text streaming. */
  CONVERSATION_STREAMING_V2 = "conversation-streaming-v2",
  /** Publishes transcript refresh events after committed writes outside a turn. */
  CONVERSATION_TIMELINE_UPDATES_ENABLED = "conversation-timeline-updates-enabled",
  /** Surfaces the cached Reducto markdown of a document whose analysis
   *  failed after parsing into the bill-checker context, so the agent can
   *  keep intake moving without a re-upload. */
  PARSED_CONTENT_ON_ANALYSIS_FAILURE = "parsed-content-on-analysis-failure",
  AI_CRAWL_STEPPER_V2_MODEL = "ai-crawl-stepper-v2-model",
  AI_CRAWL_LARGE_MODEL = "ai-crawl-large-model",
  AI_CRAWL_SMALL_MODEL = "ai-crawl-small-model",
  SIGN_IN_ROBOT_AI_MODEL = "sign-in-robot-ai-model",
  SIGN_IN_ROBOT_AI_REASONING_EFFORT = "sign-in-robot-ai-reasoning-effort",
  SIGN_IN_LOGIN_SENTINEL_MODEL = "sign-in-login-sentinel-model",
  PARSE_COMPLETER_MODEL = "parse-completer-model",
  SIGN_IN_FUSE_MFA_CODE_SUBMISSION = "sign-in-fuse-mfa-code-submission",
  SIGN_IN_MULTI_BOX_MFA = "sign-in-multi-box-mfa",
  SIGN_IN_CONCURRENT_VIEWS = "sign-in-concurrent-views",
  SIGN_IN_ROBOT_TURN_OPPORTUNITY_INTERVAL_MS = "sign-in-robot-turn-opportunity-interval-ms",
  SIGN_IN_PROGRESS_AWARE_BUDGET = "sign-in-progress-aware-budget",
  SIGN_IN_STRICT_MODE = "sign-in-strict-mode",
  SIGN_IN_AI_PROMPT = "sign-in-ai-prompt",
  SIGN_IN_TIME_OUT_IO_TOOLS = "sign-in-timeout-io-tools",
  SIGN_IN_NEOCLASSICALLY = "sign-in-neoclassically",
  SIGN_IN_HUMANIZE_CURSOR = "sign-in-humanize-cursor",
  SIGN_IN_ATTEMPT_CONTINUE = "sign-in-attempt-continue",
  SIGN_IN_LAZY = "sign-in-lazy",
  SIGN_IN_TAKEOVER_HOLD = "sign-in-takeover-hold",
  /** Enables the per-endpoint egress walk: connect/reconnect spawns one
   *  event-sourced session per ordered egress attempt and retries the next
   *  egress on a proxy-attributable pre-login failure. Off ⇒ the resolver is
   *  never consulted for a list, the plan is `RunnerResolves`, and the single
   *  attempt lets the runner resolve its own egress — byte-identical to today. */
  SIGN_IN_EGRESS_WALK = "sign-in-egress-walk",
  /** Gates whether a relink rehydrates its stored browser session. Off ⇒ the
   *  relink opens a blank context and signs in on credentials, exactly as a
   *  link with no stored session does — no Evervault decrypt, and no RESTORE
   *  stage budget to hang in. Fresh sign-ins never rehydrate and are
   *  unaffected either way. */
  SIGN_IN_SESSION_REHYDRATION = "sign-in-session-rehydration",
  /** Whether the `ParseCompleteSubSignInAgent` supervises a neoclassical
   *  sign-in, clearing modals and popups that block the flow. Ignored while
   *  `NEO_RECOVERY_ORCHESTRATOR` is on: recovery owns overlay handling then,
   *  and two actors clicking the same page concurrently is the thing that
   *  arrangement exists to avoid. */
  NEO_FALLBACK_AGENT = "neo-fallback-agent",
  /** Whether the supervision agent also runs during post-login recognition.
   *  Same subordination to `NEO_RECOVERY_ORCHESTRATOR` as the flag above. */
  NEO_EXTRA_SUPERVISION = "neo-extra-supervision",
  /**
   * Kill switch for the neoclassical recovery orchestrator. Off ⇒ sign-in runs
   * as it did before the orchestrator existed: the four tagged-union
   * recognizers are asked for no `NotMyPage` verdict and poll until their stage
   * times out, no stage quits into recovery, and the supervision agent above is
   * honoured. On ⇒ recognizers may conclude the page is wrong, those
   * conclusions and two stalls hand the page to `orchestrator.ts`, which reads
   * it and dispatches the stage it names, and supervision stands down.
   */
  NEO_RECOVERY_ORCHESTRATOR = "neo-recovery-orchestrator",
  /**
   * Whether recognition polls consult the digest gate. Off ⇒ every poll asks
   * the model, and a wrong page is concluded by
   * `CONSECUTIVE_WRONG_PAGE_VERDICTS_BEFORE_REJECTING` verdicts running, as it
   * was before the gate existed. On ⇒ a poll whose page bytes are unchanged
   * since the last judged read skips the model, and stillness after a
   * wrong-page verdict confirms it instead — same conclusion, no repeat spend.
   *
   * Independent of `NEO_RECOVERY_ORCHESTRATOR`: the gate lives in the poll loop
   * every recognizer shares, orchestrator or not.
   *
   * Worth knowing before flipping this on with the orchestrator off, since that
   * is the one combination neither flag's own description covers: a wrong page
   * confirmed by stillness is rejected, and with no orchestrator to hand it to
   * and no watchdog that covers this, that rejection ends the sign-in. That is
   * the same outcome the three-strike count produced before the gate existed —
   * the evidence differs, the ending does not — so it restores prod behaviour
   * rather than adding a new way to fail.
   */
  NEO_RECOGNITION_DIGEST_GATE = "neo-recognition-digest-gate",
  AI_CRAWL_ENABLE_CLAIM_PDF_VISITS = "ai-crawl-enable-claim-pdf-visits",
  EOB_FINDINGS_V1_ENABLED = "eob-findings-v1",
  REMOVE_INSURANCE_FROM_ONBOARDING = "remove-insurance-from-onboarding",
  SHOW_PAYMENT_IN_SETTINGS = "show-payment-in-settings",
  AI_CRAWL_SPECIALIST_INSTRUCTIONS_REF = "ai-crawl-specialist-instructions-ref",
  BOT_CONTROL_ENABLED = "bot-control-enabled",
  BENEFIT_SUMMARY_WORKFLOW_ENABLED = "benefit-summary-workflow-enabled",
  VONAGE_SMS_CC_EMAILS = "vonage-sms-cc-emails",
  /**
   * JSON config for the Vonage inbound-SMS → Slack watcher (INT-1547):
   * `{ enabled, pollSeconds, lookbackSeconds, numbers, channel }`.
   * Fails CLOSED — a missing or malformed value disables the watcher,
   * because polling on guessed values would either bill per fetched record
   * or post the wrong numbers' SMS into a staff channel.
   */
  SMS_WATCH_CONFIG = "sms-watch-config",
  PLAN_DOCUMENTS_LOADING_UI_TIMEOUT_SECONDS = "plan-documents-loading-ui-timeout-seconds",
  INSURANCE_LINKING_PROGRESS_TEXTS = "insurance-linking-progress-texts",
  SHOW_P3_ENDPOINTS = "show-p3-endpoints",
  CASE_AUTOMATION_ROUTER_WORKFLOW = "case-automation-router-workflow",
  CASE_AUTOMATION_SPECIAL_MESSAGING = "case-automation-special-messaging",
  CASE_AUTOMATION_RECORD_FACT_TOOL = "case-automation-record-fact-tool",
  CASE_AUTOMATION_MULTI_SELECTION_TOOL = "case-automation-multi-selection-tool",
  CASE_AUTOMATION_REQUEST_USER_INPUT_TOOL = "case-automation-request-user-input-tool",
  // Multivariate (DOCUMENT_REQUEST_CARD_VARIANTS). Server side, any enrolled
  // arm makes the agent ask for the bill as a `document` field; client side
  // it picks how that ask renders.
  DOCUMENT_REQUEST_CARD_EXPERIMENT = "document-request-card-experiment",
  HA_FEEDBACK_SLACK_CHANNEL = "ha-feedback-slack-channel",
  CASE_AUTOMATION_FA_AGENTIC_FLOW = "case-automation-fa-agentic-flow",
  FA_PRIOR_APPLICATION_PREFILL = "fa-prior-application-prefill",
  BILL_CHECKER_INVOICES_PRICE_TRANSPARENCY = "bill-checker-invoices-price-transparency",
  CASE_AUTOMATION_REALTIME_STREAMING = "case-automation-realtime-streaming",
  DEFERRED_AUTOMATION_BACKSTOP_DELAY_MS = "deferred-automation-backstop-delay-ms",
  SHOW_POST_ONBOARDING_REFERRAL_MODAL = "show-post-onboarding-referral-modal",
  SHOW_EHR_DEFAULT_LINKS = "show-ehr-default-links",
  CASE_AUTOMATION_RESOLUTION_CONFIDENCE = "case-automation-resolution-confidence",
  DOCUMENT_ANALYSIS_LOADING_CONFIG = "document-analysis-loading-config",
  /**
   * JSON client configuration for the post-upload read: `{ enabled, slowAfterMs }`.
   * When disabled, the client keeps the legacy wait bound and labels. When enabled,
   * `slowAfterMs` sets the quick-read slow boundary. Crossing it lets the UI offer
   * manual intake while the background read continues.
   */
  DOCUMENT_INTAKE_QUICK_READ_WAIT_CONFIG = "document-intake-quick-read-wait-config",
  POST_ESCALATION_ACTIONS_V1 = "post-escalation-actions-v1",
  /** Enables conversational questions from a pending payment-preferences card. */
  PAYMENT_PREFERENCE_OBJECTION_HANDLING = "payment-preference-objection-handling",
  /**
   * Master gate for routing high-priority cases to an HA automatically. Off
   * means classification transitions are still recorded (so the queue can be
   * measured before it is acted on) but no assignee is ever written.
   */
  HIGH_PRIORITY_AUTO_ASSIGN = "high-priority-auto-assign",
  /**
   * Who is eligible to receive an auto-assigned high-priority case:
   * `[{ "email": "...", "available": true }]`. Fails CLOSED — a missing,
   * malformed or empty list means no auto-assignment at all, because
   * assigning a high-value case to the wrong person is worse than leaving it
   * in the queue for Reo to place by hand.
   */
  HIGH_PRIORITY_ASSIGNEES = "high-priority-assignees",
  CASE_AUTOMATION_MODEL_OVERRIDES = "case-automation-model-overrides",
  CASE_AUTOMATION_EXTENDED_THINKING = "case-automation-extended-thinking",
  CASE_AUTOMATION_THINKING_BUDGET = "case-automation-thinking-budget",
  /**
   * When on, reads the issue row before building the agent context to skip
   * CHAT_MESSAGE turns that are already escalated or blocked on Natera intake.
   * Off ⇒ validation only runs after the context build, as before.
   */
  CASE_AUTOMATION_PRE_BUILD_ELIGIBILITY = "case-automation-pre-build-eligibility",
  /**
   * When on, the agent context read runs as a read-only, repeatable-read
   * transaction instead of inheriting Gel's default serializable isolation,
   * so a pure read stops joining the write conflicts on the issue row.
   * Off ⇒ default isolation, as before.
   */
  CASE_AUTOMATION_CONTEXT_READ_REPEATABLE = "case-automation-context-read-repeatable",
  CASE_AUTOMATION_FLOW_RUNNER = "case-automation-flow-runner",
  /**
   * Whether a bill case whose trusted patient responsibility is under the
   * low-responsibility threshold is routed away from advocate escalation and
   * answered with self-service guidance instead.
   *
   * Off ⇒ every path is exactly today's: the decision is never computed, no
   * tool is withheld, no action is rewritten, and no directive reaches the
   * prompt. Read against the case owner, never the caller.
   */
  CASE_AUTOMATION_LOW_RESPONSIBILITY_GATE = "case-automation-low-responsibility-gate",
  /**
   * When on, a turn that answers a deterministic flow's question loads only
   * the slice of the context that flow reads, and routes to the flow runner on
   * the case view alone rather than after a full context build. The full
   * context is still built when the flow yields to the routed agent, and for
   * the turn's own post-reply work — after the member's next question is sent,
   * not before it.
   *
   * Off ⇒ every flow turn builds the whole context before anything routes.
   * Read against the case owner. Requires CASE_AUTOMATION_FLOW_RUNNER: with no
   * flow runner there is no flow turn to narrow.
   */
  CASE_AUTOMATION_NARROW_FLOW_CONTEXT = "case-automation-narrow-flow-context",
  /**
   * Whether a case whose confirmed provider is Natera gets Natera's own
   * handling rather than the generic bill-case handling.
   *
   * Deliberately named for the segment, not for one surface: the
   * post-escalation checklist is the first consumer, and the intake questions
   * the agent asks are the next. A second flag per surface would let a case
   * run half of Natera's treatment.
   *
   * Off ⇒ the case is handled exactly as any other bill case. Read against the
   * case owner, never the caller — an HA approving a suggested action must not
   * flip the cohort.
   */
  NATERA_INTAKE = "natera-intake",
  /**
   * Whether a Natera case builds its post-escalation checklist BEFORE handing
   * off, and withholds the hand-off until no required row is outstanding.
   *
   * A sub-flag of NATERA_INTAKE rather than a widening of it: the intake ships
   * on, and the gate wants its own cohort and its own rollback because the
   * failure it introduces is the invisible one. An intake that dies partway
   * through is loud — the case escalates anyway and an HA picks it up —
   * whereas a case held on a checklist that never empties simply never
   * escalates: the advocate never sees it and the member believes they are in
   * the queue. (ENG-7902, the projection race that killed the intake often
   * enough to block this outright, landed 2026-08-21.)
   *
   * Off ⇒ the case escalates the moment the intake completes, exactly as
   * before, and the checklist is built by the escalation as it always was.
   * Read against the case owner, never the caller.
   */
  NATERA_ESCALATION_GATE = "natera-escalation-gate",
  /**
   * Whether a new guided Natera case enrolls on the delayed-card route:
   * fee acknowledgement in the hub, card collected later. Read at case
   * creation against the member (`userId`) and stamped onto `Issue.labels`.
   * Off (the code default) leaves new cases on the current card-up-front
   * flow. An already-labeled case keeps its route after rollback.
   */
  NATERA_DELAYED_CARD = "natera-delayed-card",
  /**
   * Whether a new funnel case collects its activation asks through the chat
   * intake flow instead of the activation hub.
   *
   * Read by the CLIENT, which passes the resulting mode to
   * `createNateraIntakeCase`: a native binary that predates chat mode never
   * asks for it, so it can never be handed a case with no hub. The server
   * stamps `NATERA_GUIDED_CHAT_LABEL` on the case it opens, and every later
   * read branches on that label rather than on this flag, so a case keeps its
   * route after a rollback.
   *
   * Off (the code default) opens the case on the hub, exactly as before.
   */
  NATERA_GUIDED_CHAT_ACTIVATION = "natera-guided-chat-activation",
  /**
   * Whether a new general payment-preferences ask enrolls on the simplified
   * contract: fee acknowledgement, provider relationship, and note only, with
   * the ceiling, accept-any, autopay, and payment-plan questions dropped. Read
   * once at request creation against the case owner and stamped onto the
   * request's `contract`, so a stamped request keeps its shape after rollback.
   * Off (the code default) leaves new asks on the full contract. Every new
   * request follows the flag, including the funnel's fee-acknowledgement
   * request; the delayed-card guard only refuses non-fee-ack requests, it does
   * not force a contract.
   */
  SIMPLIFY_GENERAL_PAYMENTS = "simplify-general-payments",
  CASE_TRIAGE_SHADOW = "case-triage-shadow",
  /**
   * Activation gate for the Suggest rung: whether a live triage decision may
   * actually FILE a SuggestedAction on the review task. Separate from
   * CASE_TRIAGE_SHADOW (which only gates measurement) so the shadow measurement
   * cohort doesn't silently start producing patient-facing suggestions when a
   * verb's rung flips to `suggest`. Off ⇒ decisions are computed and logged but
   * no suggestion is filed.
   */
  CASE_TRIAGE_SUGGEST = "case-triage-suggest",
  /**
   * Activation gate for the DRAFTING verbs at the Suggest rung —
   * `message_member_update` and `other`, the two that put member-facing prose
   * in front of an advocate. `no_op` is unaffected and stays on
   * CASE_TRIAGE_SUGGEST alone.
   *
   * A third flag rather than reusing the second, because the second is already
   * on at 100% in prod for `no_op`: without this, adding a drafting verb to
   * VERB_POLICY would activate it for every advocate on merge, with no flip and
   * no cohort. That is exactly what splitting shadow from suggest was meant to
   * prevent, and the same reasoning applies again a rung later.
   *
   * Off ⇒ the decision is computed and logged, the review task is filed as
   * usual, and no draft is surfaced. Read against the case owner, never the
   * caller — an advocate opening a case must not flip the cohort.
   */
  CASE_TRIAGE_DRAFT_REPLY = "case-triage-draft-reply",
  /**
   * Extends the drafted reply from chat messages to escalated member document
   * uploads. Its own gate rather than riding CASE_TRIAGE_DRAFT_REPLY because
   * the two triggers are not the same bet: a chat message is a question that
   * usually wants an answer, while an upload is often one of several files, a
   * duplicate, or exactly the document the advocate asked for and will act on
   * after reading it.
   *
   * The chat path also asks the triage classifier whether a reply is warranted
   * at all and can answer `no_op`; the upload path has no such judgement and
   * drafts unconditionally. Keeping the flags separate is what lets the upload
   * cohort be measured against the chat baseline before deciding whether that
   * judgement step needs to be built here too.
   *
   * Requires CASE_TRIAGE_DRAFT_REPLY to also be on — this gate sits downstream
   * of it. Read against the case owner, same as its parent.
   */
  CASE_TRIAGE_DRAFT_ON_UPLOAD = "case-triage-draft-on-upload",
  /**
   * Capture-only gate for the post-escalation reply corpus. When on, every
   * post-escalation inbound member message generates a reply draft that is
   * traced to Langfuse and then discarded. Never surfaces anything to an HA —
   * surfacing is a separate, later gate — so this can be rolled to 100%
   * without changing what any member or advocate sees.
   */
  POST_ESCALATION_DRAFT_SHADOW = "post-escalation-draft-shadow",
  /**
   * Per-member send gate for the in-app re-engagement outreach. Read against
   * the case owner, once per candidate case. Off suppresses the send; the scan
   * still counts the case, so the reach is measurable before anything ships.
   */
  CASE_OUTREACH = "case-outreach",
  /**
   * Number flag: the share of members held out of case outreach as the
   * control arm, in [0, 1]. Read once per hourly run. The code default is
   * 0.5; converting the control arm to treatment is an edit to this flag,
   * not a release.
   */
  CASE_OUTREACH_HOLDOUT_SHARE = "case-outreach-holdout-share",
  FAKE_DOOR_MMFA = "fake-door-mmfa",
  WEB_AGENT_PROVIDER_MANAGED_PERSISTENCE = "web-agent-provider-managed-persistence",
  /** Kept with a single live arm (`v3.0`) as the slot for the next onboarding
   *  experiment. Do not archive while pre-flip builds are live: those binaries
   *  bundle their own default and fall back to the retired `v2.0`. */
  USE_ONBOARDING_FLOW = "use-onboarding-flow",
  /** Pre-signup routing questions at `/get-started` (web). Evaluated pre-auth
   *  only — post-auth behavior keys on the recorded answers, never on this
   *  flag, because the LD context key changes at login. */
  INTAKE_ROUTING = "intake-routing",
  /** Where a member who confirmed a Natera bill lands ("natera-intake" vs
   *  "onboarding"). Pre-auth only, like INTAKE_ROUTING above. */
  INTAKE_ROUTING_NATERA_DESTINATION = "intake-routing-natera-destination",
  /** Bill-intent onboarding v3 completion lands on "home" or a bill-check "case". */
  ONBOARDING_BILL_COMPLETION_DESTINATION = "onboarding-bill-completion-destination",
  /** "How Granted works" screen before the Natera intake's first bill
   *  question. */
  NATERA_INTAKE_PLAYBOOK = "natera-intake-playbook",
  /** Whether the Natera intake asks for the member's bill story after the
   *  result screen. */
  NATERA_INTAKE_STORY = "natera-intake-story",
  COMPUTE_HA_AVERAGE_RATING = "compute-ha-average-rating",
  CHAT_ERROR_ALERTS = "chat-error-alerts",
  /** Runtime kill-switch for multi-document splitting (Reducto `/split`).
   *  Off routes every packet through the passthrough (single-document)
   *  path — the same behavior as a cache-hit parse or a 0–1 segment
   *  result — so a bad split can be killed without a deploy. */
  SPLIT_MULTIDOC_ENABLED = "split-multidoc-enabled",
  /**
   * Advisor `provider://` backend: `"gel"` (default) or `"files"` (markdown
   * corpus fallback). String enum — flip in LD without a deploy. Local
   * `PROVIDER_INTEL_SOURCE=gel|files` still overrides when set.
   */
  PROVIDER_INTEL_SOURCE = "provider-intel-source",
  /**
   * Master gate for the headless provider research runner. Off ⇒ the activity
   * marks its run Failed and returns before a session is started, so no run
   * ever spends. Read against a fixed anonymous context, never a user.
   */
  PROVIDER_RESEARCH_ENABLED = "provider-research-enabled",
  /**
   * Whether a finished research run opens its review task. Separate from the
   * master gate because the two answer different questions: that one decides
   * whether research happens at all, this one decides whether an advocate is
   * handed priority-1 work about it. Off ⇒ runs still research and still write
   * their notes, and the verdict thumbs are the only feedback path. Defaults
   * off, and read against the same fixed anonymous context.
   */
  PROVIDER_RESEARCH_TASKS = "provider-research-tasks",
  /**
   * Where a ParseResult write puts the markdown: `inline` (default), `dual`
   * (S3 and inline, for the migration) or `s3` (pointer only). Gates writes
   * only. Reads take S3 when the row has a key, else the inline body.
   */
  PARSE_MARKDOWN_STORAGE = "parse-markdown-storage",
  /** Runs Stedi Insurance Discovery automatically after an onboarding-form
   *  membership fails its eligibility check for a reason discovery can fix.
   *  Read by the discovery worker before the vendor call; off means the job
   *  completes as an unbilled no-op and nothing durable is written. */
  INSURANCE_DISCOVERY_ONBOARDING_FALLBACK = "insurance-discovery-onboarding-fallback",
  /**
   * JSON map of marketing initiative ids to whether that campaign is live.
   * Context construction reads it once per packet, against a fixed anonymous
   * context, and appends only the campaigns whose value is true. The code
   * default is `{}`, so a missing flag adds nothing.
   *
   * Known id: `clear-the-bill` (Clear the Bill,
   * https://grantedhealth.com/texas). Unknown ids are ignored. This is not a
   * per-member cohort: set the environment's default rule to turn a campaign
   * on for the in-app case agents at once. Anonymous pre-auth flows do not
   * read it.
   */
  MARKETING_INITIATIVES = "marketing-initiatives",
}

/** Values of the `webAgent::EndpointSupportLevel` Gel enum, mirrored here to
 *  keep this client-shared module free of server-lib dependencies. Drift from
 *  the source enum fails compilation in consumers: the server assigns
 *  Gel-typed values directly, and the client bridges through exhaustive
 *  `Record` maps keyed by the generated GraphQL enum. */
export type EndpointSupportLevel = "P0" | "P1" | "P2" | "P3";

/** Values of the `webAgent::EndpointType` Gel enum, mirrored here to keep
 *  this client-shared module free of server-lib dependencies. Same
 *  compile-time drift guarantee as {@link EndpointSupportLevel}. */
export type EndpointType = "EHR" | "INSURANCE";

/** Targeting attributes carried on the `endpointSlug` context kind, so
 *  LaunchDarkly rules can match cohorts (e.g. all P0 portals) instead of
 *  enumerating slugs. */
export type EndpointSlugAttributes = {
  supportLevel?: EndpointSupportLevel;
  type?: EndpointType;
};

type ContextShapes = {
  endpointSlug: LDContextCommon & EndpointSlugAttributes;
  user: LDContextCommon;
  toPhoneNumber: LDContextCommon;
  executionId: LDContextCommon;
  /** The `webAgent::CDPProvider` actually driving the browser session, so rules
   *  can target a provider × portal pair. Supplied at the point of use rather
   *  than by `flagContextForExecution`: the execution's context is built before
   *  a browser session exists, and hedging can land a provider other than the
   *  one first resolved. */
  cdpProvider: LDContextCommon;
};

export type Contexts = keyof ContextShapes;

/** Acceptable contexts for LaunchDarkly. */
export type KnownLDContext = { kind: "multi" } & {
  // This enforces that at least one of the Context properties is present.
  // Otherwise, LD's client will error at runtime.
  [K in Contexts]: { [P in K]: ContextShapes[P] } & {
    [P in Exclude<Contexts, K>]?: ContextShapes[P];
  };
}[Contexts];

({}) as KnownLDContext satisfies LDContext;

export const ANONYMOUS_KEY_COOKIE = "mbai_ld_anon";
export const ANONYMOUS_KEY_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/** Server-evaluated flag snapshot handed to a wrapped page's first render. */
export type FlagBootstrap =
  | { status: "ready"; context: LDContext; flags: Record<string, unknown> }
  | {
      status: "unavailable";
      reason:
        | "timeout"
        | "evaluation-error"
        | "serialization-error"
        | "identity-unavailable"
        | "not-configured";
    };

export const EMPTY_LD_CONTEXT: KnownLDContext = {
  kind: "multi",
  user: { key: "empty" },
};

// `legacy` is the default (not enrolled, or LD unreachable): the agent asks
// for the bill in prose. Enrolled users split between `experiment-message-only`
// (control: a document ask rendered as its question) and `experiment-card`
// (treatment: the upload card). Both arms emit the same event; their prompt
// bodies differ only in how the user is told to send the document.
export const DOCUMENT_REQUEST_CARD_VARIANTS = [
  "legacy",
  "experiment-message-only",
  "experiment-card",
] as const;
export type DocumentRequestCardVariant =
  (typeof DOCUMENT_REQUEST_CARD_VARIANTS)[number];

// The `experiment-*` twins are served ONLY by a running LD experiment, so
// enrolled traffic is comparable without windowing on timestamps; the bare
// variants are the operational defaults. Rendering treats each pair
// identically via `stripExperimentVariant`.
export const INTAKE_ROUTING_VARIANTS = [
  "routing",
  "control",
  "experiment-routing",
  "experiment-control",
] as const;
export type IntakeRoutingVariant = (typeof INTAKE_ROUTING_VARIANTS)[number];

export const INTAKE_ROUTING_NATERA_DESTINATIONS = [
  "natera-intake",
  "onboarding",
  "experiment-natera-intake",
  "experiment-onboarding",
] as const;
export type IntakeRoutingNateraDestination =
  (typeof INTAKE_ROUTING_NATERA_DESTINATIONS)[number];

export const ONBOARDING_BILL_COMPLETION_DESTINATIONS = [
  "home",
  "case",
  "experiment-home",
  "experiment-case",
] as const;
export type OnboardingBillCompletionDestination =
  (typeof ONBOARDING_BILL_COMPLETION_DESTINATIONS)[number];

export const NATERA_INTAKE_PLAYBOOK_VARIANTS = [
  "control",
  "playbook",
  "experiment-control",
  "experiment-playbook",
] as const;
export type NateraIntakePlaybookVariant =
  (typeof NATERA_INTAKE_PLAYBOOK_VARIANTS)[number];

export const NATERA_INTAKE_STORY_VARIANTS = [
  "control",
  "no-story",
  "experiment-control",
  "experiment-no-story",
] as const;
export type NateraIntakeStoryVariant =
  (typeof NATERA_INTAKE_STORY_VARIANTS)[number];

/** Collapses an `experiment-*` variation onto its operational twin. */
export const stripExperimentVariant = (value: string): string =>
  value.startsWith("experiment-") ? value.slice("experiment-".length) : value;
